import React, { useRef, useEffect, useState } from "react";
import Header from "../components/Header";
import RequiredStar from "../components/RequiredStar"; // IMPORTED
import {
  Trash2,
  Plus,
  ArrowRight,
  ArrowLeft,
  FileText,
  Bold,
  Underline,
  Highlighter,
} from "lucide-react";

const pageStyles = `:root {
  --primary: #084bcc;
  --primary-hover: #063699;
  --muted: #6b7280;
  --bg: #f6f9ff;
  --card-bg: #ffffff;
  --border: #e6eefc;
  --text: #0f172a;
  --danger: #ef4444;
  --success: #10b981;
}
.main-content { max-width: 1100px; margin: 1.25rem auto; padding: 0 1rem 1rem; }
.page-container { animation: fadeIn 0.28s ease-in-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

/* HEADER & BUTTONS */
.top-header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem; }
.btn-back-top { padding: 0.5rem 1rem; background: white; border: 1px solid var(--border); border-radius: 8px; color: var(--text); font-weight: 700; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; transition: all 0.2s; height: 40px; }
.btn-back-top:hover { border-color: var(--primary); color: var(--primary); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(8,75,204,0.08); }

.form-card { background: var(--card-bg); padding: 1.5rem; border-radius: 12px; box-shadow: 0 6px 20px rgba(3,37,86,0.035); margin-bottom: 1rem; border: 1px solid var(--border); }
.section-title { margin: 0; color: var(--primary-hover); padding-bottom: 0.125rem; font-size: 1.5rem; font-weight: 900; display: flex; align-items: center; gap: 0.75rem; }
.section-sub { margin: 0.25rem 0 0 0; color: var(--muted); font-size: 1rem; }
.section-divider { height: 1px; background: var(--border); border-radius: 2px; margin: 1rem 0 1.5rem; }

.form-group { margin-bottom: 1.05rem; }
label { display: flex; align-items: center; margin-bottom: 0.5rem; font-weight: 600; font-size: 0.95rem; color: #0b1736; }
input, select { width: 100%; padding: 0.75rem; border: 1px solid var(--border); border-radius: 8px; font-size: 1rem; box-sizing: border-box; transition: border-color 0.16s, box-shadow 0.16s; font-family: inherit; }
input:focus, select:focus { outline: none; border-color: var(--primary); box-shadow: 0 6px 20px rgba(8,75,204,0.06); }

/* --- NEW ERROR STYLE --- */
.input-error { 
  border-color: var(--danger) !important; 
  background-color: #fff5f5; 
}
.input-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
}

.internal-badge { display: inline-block; background: rgba(8,75,204,0.08); color: var(--primary-hover); padding: 0.2rem 0.5rem; border-radius: 999px; font-size: 0.8rem; font-weight: 700; margin-left: 0.5rem; }
.field-note { font-size: 0.85rem; color: var(--muted); margin-top: 0.5rem; }

.dynamic-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px; }
.dynamic-input-row { display: flex; gap: 8px; align-items: center; }
.btn-primary, .btn-secondary { padding: 0.75rem 1.25rem; border-radius: 8px; font-weight: 700; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 8px; transition: all 0.16s ease; }
.btn-primary { background: var(--primary); color: white; box-shadow: 0 8px 20px rgba(8,75,204,0.08); border: 1px solid rgba(0,0,0,0.04); }
.btn-primary:hover { background: var(--primary-hover); transform: translateY(-1px); }
.btn-secondary { background: white; border: 1px solid var(--border); color: var(--muted); }
.nav-buttons .btn-secondary { color: var(--primary); border-color: var(--primary); }
.btn-dashed-small { background: transparent; border: 1px dashed rgba(8,75,204,0.18); color: var(--primary); padding: 8px 12px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 0.95rem; font-weight: 700; transition: background 0.14s; }
.btn-dashed-small:hover { background: rgba(8,75,204,0.06); transform: translateY(-1px); }
.btn-icon-danger { background: #fff5f5; color: var(--danger); border: none; padding: 8px; border-radius: 6px; cursor: pointer; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

/* RICH TEXT EDITOR */
.rich-editor-container { border: 1px solid var(--border); border-radius: 8px; overflow: hidden; background: white; transition: border-color 0.2s; }
.rich-editor-container:focus-within { border-color: var(--primary); box-shadow: 0 6px 20px rgba(8,75,204,0.06); }
.rich-toolbar { display: flex; gap: 8px; background: #f8fafc; padding: 8px 12px; border-bottom: 1px solid var(--border); }
.rich-btn { background: white; border: 1px solid #cbd5e1; padding: 6px; border-radius: 4px; cursor: pointer; color: #475569; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.rich-btn:hover { background: #e2e8f0; color: var(--primary); border-color: var(--primary); }
.rich-content { padding: 12px; min-height: 120px; outline: none; line-height: 1.6; color: #334155; }
.toolbar-divider { width: 1px; height: 20px; background: #e5e7eb; margin: 0 6px; }
.toggle-container { display: flex; align-items: center; cursor: pointer; margin-left: 6px; padding: 4px 8px; border-radius: 6px; transition: background 0.2s; }
.toggle-container:hover { background: #e6eefc; }
.toggle-switch { position: relative; width: 36px; height: 20px; background-color: #94a3b8; border-radius: 20px; margin-right: 8px; transition: background-color 0.3s; flex-shrink: 0; }
.toggle-switch.on { background-color: var(--primary); }
.toggle-thumb { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; background-color: white; border-radius: 50%; transition: transform 0.3s; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.toggle-switch.on .toggle-thumb { transform: translateX(16px); }
.toggle-text { font-size: 0.85rem; color: #334155; font-weight: 600; user-select: none; }

/* LAYOUT */
.layout-grid { display: grid; grid-template-columns: 1fr 360px; gap: 0.9rem; align-items: start; }
.summary-card { background: linear-gradient(180deg, #fbfdff, #f4f9ff); border-radius: 10px; border: 1px solid var(--border); padding: 1rem; box-shadow: 0 6px 14px rgba(3,37,86,0.03); position: sticky; top: 96px; }
.summary-title { font-size: 1.08rem; font-weight: 800; color: var(--primary-hover); margin-bottom: 0.65rem; }
.summary-item { font-size: 0.94rem; color: var(--text); margin-bottom: 0.6rem; display: flex; align-items: center; gap: 12px; }
.summary-item .label { color: var(--muted); font-weight: 700; font-size: 0.92rem; flex: 0 0 100px; }
.summary-item .value { color: var(--text); font-weight: 600; font-size: 0.94rem; flex: 1 1 auto; text-align: right; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-right: 6px; }
.status { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 999px; font-size: 14px; color: white; background: #cbd5d6; flex: 0 0 34px; }
.status.ok { background: #10b981; }
.char-count { margin-left: auto; font-size: 0.85rem; color: var(--muted); }

@media (max-width: 980px) { .layout-grid { grid-template-columns: 1fr; } .summary-card { position: static; margin-top: 1rem; } }
@media (max-width: 600px) { .top-header-row { flex-direction: column; align-items: flex-start; gap: 1rem; } .btn-back-top { width: 100%; justify-content: center; } }
`;

const RichTextEditor = ({ value, onChange, placeholder, className }) => {
  const editorRef = useRef(null);
  const [retainFormat, setRetainFormat] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      if (editorRef.current.innerHTML !== value) {
        if (value === "" || document.activeElement !== editorRef.current) {
          editorRef.current.innerHTML = value || "";
        }
      }
    }
  }, [value]);

  const execCmd = (command, val = null) => {
    document.execCommand(command, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handlePaste = (e) => {
    if (!retainFormat) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className={`rich-editor-container ${className || ""}`}>
      <div className="rich-toolbar">
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            execCmd("bold");
          }}
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            execCmd("underline");
          }}
          title="Underline"
        >
          <Underline size={16} />
        </button>
        <button
          type="button"
          className="rich-btn"
          onMouseDown={(e) => {
            e.preventDefault();
            execCmd("hiliteColor", "#ffff00");
          }}
          title="Highlight"
        >
          <Highlighter size={16} style={{ color: "#ffff00" }} />
        </button>
        <div className="toolbar-divider" />
        <div
          className="toggle-container"
          onClick={() => setRetainFormat(!retainFormat)}
          title="Toggle Paste Format"
        >
          <div className={`toggle-switch ${retainFormat ? "on" : ""}`}>
            <div className="toggle-thumb" />
          </div>
          <div className="toggle-text">
            {retainFormat ? "Plain Text" : "Keep Format"}
          </div>
        </div>
      </div>
      <div
        className="rich-content"
        contentEditable
        ref={editorRef}
        onInput={() => onChange(editorRef.current.innerHTML)}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const dateObj = new Date(year, month - 1, day);
  const dayNum = parseInt(day);
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };
  return `${dayNum}${getOrdinal(dayNum)} ${dateObj.toLocaleString("default", {
    month: "long",
  })} ${year}`;
};

const getTodayDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const MetadataPage = ({ data, updateData, next, back, onLogout }) => {
  const mainRef = useRef(null);
  const [showErrors, setShowErrors] = useState(false); // NEW STATE

  const introWords = stripHtml(data.introduction || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  const featureCount = (data.features || []).filter(
    (f) => f && f.text && f.text.trim() !== ""
  ).length;
  const keywordCount = (data.keywords || []).filter(
    (k) => k && k.text && k.text.trim() !== ""
  ).length;
  const introFilled = introWords > 0;
  const hasTitle = data.title && data.title.trim();
  const hasIntro = introFilled;
  const hasFeature = featureCount > 0;
  const metadataValid = hasTitle && hasIntro && hasFeature;
  useEffect(() => {
    try {
      if (typeof window !== "undefined") window.scrollTo(0, 0);
    } catch (e) {}
  }, []);

  // Set default report date to today if not already set
  useEffect(() => {
    if (
      (!data.reportDate || data.reportDate === "") &&
      typeof updateData === "function"
    ) {
      updateData("reportDate", getTodayDate());
    }
  }, [data.reportDate, updateData]);

  const handleFeatureChange = (id, value) =>
    updateData(
      "features",
      data.features.map((f) => (f.id === id ? { ...f, text: value } : f))
    );
  const addFeatureLine = () =>
    updateData("features", [...data.features, { id: Date.now(), text: "" }]);
  const removeFeature = (id) =>
    updateData(
      "features",
      data.features.filter((f) => f.id !== id)
    );

  const handleKeywordChange = (id, value) =>
    updateData(
      "keywords",
      data.keywords.map((k) => (k.id === id ? { ...k, text: value } : k))
    );
  const addKeywordLine = () =>
    updateData("keywords", [...data.keywords, { id: Date.now(), text: "" }]);
  const removeKeyword = (id) =>
    updateData(
      "keywords",
      data.keywords.filter((k) => k.id !== id)
    );

  // Validation Logic
  const handleNext = () => {
    const hasTitle = data.title && data.title.trim();
    const hasIntro = introFilled;
    const hasFeature = featureCount > 0;

    if (!hasTitle || !hasIntro || !hasFeature) {
      setShowErrors(true);
      // scroll to top so user sees validation
      if (mainRef.current) mainRef.current.scrollTop = 0;
      return;
    }

    next();
  };

  return (
    <>
      <style>{pageStyles}</style>
      <Header active="metadata" onLogout={onLogout} />
      <div className="main-content" ref={mainRef}>
        <div className="page-container">
          <div className="layout-grid">
            <div className="form-card">
              <div className="top-header-row">
                <div>
                  <h2 className="section-title">
                    <FileText size={24} /> Project Metadata
                  </h2>
                  <p className="section-sub">Enter the project details.</p>
                </div>
                <button className="btn-back-top" onClick={back}>
                  <ArrowLeft size={16} /> Back
                </button>
              </div>
              <div className="section-divider" />

              <div className="form-group">
                <label>
                  Project ID <span className="internal-badge">Internal</span>
                </label>
                <input
                  value={data.internalProjectId || ""}
                  onChange={(e) =>
                    updateData("internalProjectId", e.target.value)
                  }
                  placeholder="Internal Project identifier"
                />
              </div>

              <div className="form-group">
                <label>
                  Created By <span className="internal-badge">Internal</span>
                </label>
                <input
                  value={data.createdBy || ""}
                  onChange={(e) => updateData("createdBy", e.target.value)}
                  placeholder="Your name or initials"
                />
              </div>

              <div className="form-group">
                <label>Date of Report</label>
                <input
                  type="date"
                  value={data.reportDate || ""}
                  onChange={(e) => updateData("reportDate", e.target.value)}
                />
              </div>

              {/* TITLE FIELD WITH RED VALIDATION */}
              <div className="form-group">
                <label>
                  Project Title <RequiredStar />
                </label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <select
                    value={data.titlePrefix || "A"}
                    onChange={(e) => updateData("titlePrefix", e.target.value)}
                    style={{
                      width: "100px",
                      flexShrink: 0,
                      background: "white",
                      cursor: "pointer",
                    }}
                  >
                    <option value="A">A</option>
                    <option value="An">An</option>
                    <option value="The">The</option>
                  </select>
                  <input
                    value={data.title}
                    onChange={(e) => updateData("title", e.target.value)}
                    placeholder="Enter full project title..."
                    style={{ flex: 1 }}
                    className={
                      showErrors && (!data.title || !data.title.trim())
                        ? "input-error"
                        : ""
                    }
                  />
                </div>
                {showErrors && (!data.title || !data.title.trim()) && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "0.8rem",
                      marginTop: "6px",
                    }}
                  >
                    Project Title is required to proceed.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  Introduction <RequiredStar />
                  <span className="char-count">{introWords} words</span>
                </label>
                <RichTextEditor
                  value={data.introduction}
                  onChange={(val) => updateData("introduction", val)}
                  placeholder="Brief introduction..."
                  className={showErrors && !introFilled ? "input-error" : ""}
                />
                {showErrors && !introFilled && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "0.85rem",
                      marginTop: "6px",
                    }}
                  >
                    Introduction is required.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>
                  Key Features <RequiredStar />
                </label>
                <div className="dynamic-list">
                  {data.features.map((f) => (
                    <div key={f.id} className="dynamic-input-row">
                      <input
                        value={f.text}
                        onChange={(e) =>
                          handleFeatureChange(f.id, e.target.value)
                        }
                        placeholder="Type a feature..."
                        className={
                          showErrors && (!f.text || !f.text.trim())
                            ? "input-error"
                            : ""
                        }
                      />
                      <button
                        className="btn-icon-danger"
                        onClick={() => removeFeature(f.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                {showErrors && featureCount === 0 && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "0.85rem",
                      marginTop: "6px",
                    }}
                  >
                    Please add at least one feature.
                  </div>
                )}
                <button className="btn-dashed-small" onClick={addFeatureLine}>
                  <Plus size={16} /> Add Feature
                </button>
              </div>

              <div className="form-group">
                <label>Keywords</label>
                <div className="dynamic-list">
                  {data.keywords.map((k) => (
                    <div key={k.id} className="dynamic-input-row">
                      <input
                        value={k.text}
                        onChange={(e) =>
                          handleKeywordChange(k.id, e.target.value)
                        }
                        placeholder="Type a keyword..."
                      />
                      <button
                        className="btn-icon-danger"
                        onClick={() => removeKeyword(k.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <button className="btn-dashed-small" onClick={addKeywordLine}>
                  <Plus size={16} /> Add Keyword
                </button>
              </div>

              <div className="nav-buttons">
                <div style={{ display: "flex", width: "100%", gap: 12 }}>
                  <button className="btn-secondary" onClick={back}>
                    <ArrowLeft size={16} /> Back
                  </button>
                  <div style={{ marginLeft: "auto" }}>
                    {/* BUTTON ENABLED, VALIDATION ON CLICK */}
                    <button className="btn-primary" onClick={handleNext}>
                      Next Step <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
                {showErrors && !metadataValid && (
                  <div
                    style={{ color: "#ef4444", fontWeight: 700, marginTop: 10 }}
                  >
                    Please fill all required fields before proceeding.
                  </div>
                )}
              </div>
            </div>

            <aside className="summary-card">
              <div className="summary-title">Project Summary</div>
              <div className="summary-item">
                <div className="label">Project ID</div>
                <div className="value">{data.internalProjectId || "—"}</div>
                <div
                  className={"status " + (data.internalProjectId ? "ok" : "")}
                >
                  {data.internalProjectId ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Created By</div>
                <div className="value">{data.createdBy || "—"}</div>
                <div className={"status " + (data.createdBy ? "ok" : "")}>
                  {data.createdBy ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Report Date</div>
                <div className="value">
                  {data.reportDate ? formatDate(data.reportDate) : "—"}
                </div>
                <div className={"status " + (data.reportDate ? "ok" : "")}>
                  {data.reportDate ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Title</div>
                <div className="value">{data.title || "—"}</div>
                <div className={"status " + (data.title ? "ok" : "")}>
                  {data.title ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Introduction</div>
                <div className="value">
                  {introWords ? `${introWords} words` : "—"}
                </div>
                <div className={"status " + (introWords ? "ok" : "")}>
                  {introWords ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Features</div>
                <div className="value">{featureCount} items</div>
                <div className={"status " + (featureCount > 0 ? "ok" : "")}>
                  {featureCount > 0 ? "✓" : "—"}
                </div>
              </div>
              <div className="summary-item">
                <div className="label">Keywords</div>
                <div className="value">{keywordCount} items</div>
                <div className={"status " + (keywordCount > 0 ? "ok" : "")}>
                  {keywordCount > 0 ? "✓" : "—"}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetadataPage;
